import * as L from 'leaflet';
import { HttpClient } from '@angular/common/http';
import { FeatureDisplayItem } from '../featureDisplay/featureDisplayItem';
import { FeatureDisplayItemGenerator } from '../featureDisplay/featureDisplayItemGenerator';
import { HtmlFeatureIdentifier } from '../featureDisplay/htmlFeatureIdentifier';
import { JsonFeatureIdentifier } from '../featureDisplay/jsonFeatureIdentifier';
import { WmsTileLayer } from './wmsTileLayer';
import 'jquery';

export enum WmsFeatureFormat {
  JSON = 'application/json',
  GEO_JSON = 'application/geojson',
  PLAIN_TEXT = 'text/plain',
  HTML_TEXT = 'text/html',
}

export class WmsFeatureDisplayItemGenerator implements FeatureDisplayItemGenerator {
  protected itemGenerators = new Map<WmsFeatureFormat, FeatureDisplayItemGenerator>();
  protected preferredFormats = new Array<WmsFeatureFormat>();
  protected allowedFormats: Array<WmsFeatureFormat>;
  protected selectedFormat: null | WmsFeatureFormat;
  protected featureCallParams = new Map<string, string>();

  constructor(protected layer: WmsTileLayer) {
    // console.debug('construct', this.layer, layer);
    this.setDefaults();
  }

  public static getEnumFromValue<T>(value: string | number, enumerator: Record<string, unknown>): null | T {
    // get the string key from the value
    const key = Object.keys(enumerator).find((thisKey: string | number) => enumerator[thisKey] === value);
    return null != key ? (enumerator[key] as T) : null;
  }

  public static createUrl(
    layer: WmsTileLayer,
    clickEvent: L.LeafletMouseEvent,
    overrideParamsFunction?: () => Map<string, string>,
  ): string {
    // console.debug('createUrl', layer);
    const bgsLeaflet = layer.getBgsLeaflet();
    if (null != bgsLeaflet) {
      const bounds = bgsLeaflet.getMapExtent();
      const mapSize = bgsLeaflet.getMapSize();

      const parameters = new Map<string, null | string>();
      parameters.set('request', 'GetFeatureInfo');
      parameters.set('service', 'WMS');
      parameters.set('version', layer.options.get('version') ? layer.options.get('version') : null);
      parameters.set('x', clickEvent.containerPoint.round().x.toString());
      parameters.set('i', clickEvent.containerPoint.round().x.toString()); // backwards compatible x
      parameters.set('y', clickEvent.containerPoint.round().y.toString());
      parameters.set('j', clickEvent.containerPoint.round().y.toString()); // backwards compatible y
      parameters.set('layers', layer.options.get('layers'));
      parameters.set('query_layers', layer.options.get('layers'));
      parameters.set('styles', layer.options.get('styles') ? layer.options.get('styles') : null);
      parameters.set('crs', layer.options.get('crs') ? layer.options.get('crs') : 'CRS:84');
      parameters.set('bbox', bounds.asArray().reverse().join(','));
      parameters.set('width', mapSize.widthPx.toString());
      parameters.set('height', mapSize.heightPx.toString());
      parameters.set('feature_count', layer.options.get('feature_count') ? layer.options.get('feature_count') : null);
      // parameters.set('info_format', (layer.options.get('info_format')) ? layer.options.get('info_format') : 'application/geojson');

      if (null != overrideParamsFunction) {
        overrideParamsFunction().forEach((value: string, key: string) => {
          parameters.set(key, value);
        });
      }

      const url =
        layer.url +
        '?' +
        Array.from(parameters.keys())
          .filter((key) => null != parameters.get(key))
          .map((key) => `${key}=${parameters.get(key)}`)
          .join('&');

      return url.replace(/ /g, '');
    } else {
      return '';
    }
  }

  public setPreferredFormats(formats: Array<WmsFeatureFormat>): this {
    this.preferredFormats = formats.slice();
    return this;
  }
  public setFormatHandler(format: WmsFeatureFormat, itemGenerator: FeatureDisplayItemGenerator): this {
    this.itemGenerators.set(format, itemGenerator);
    return this;
  }
  public getFeatureDisplayItems(clickEvent: L.LeafletMouseEvent, http: HttpClient): Promise<Array<FeatureDisplayItem>> {
    return (null != this.selectedFormat ? Promise.resolve(this.selectedFormat) : this.getFormat(http)).then(
      (format) => {
        this.selectedFormat = format;
        const itemGenerator = null == format ? null : this.itemGenerators.get(format);
        // console.debug('format', format, this.selectedFormat, itemGenerator);
        return null == itemGenerator ? [] : itemGenerator.getFeatureDisplayItems(clickEvent, http);
      },
    );
  }

  public setFeatureCallParams(params: Map<string, string>): this {
    this.featureCallParams = new Map<string, string>();

    if (null != params) {
      params.forEach((value: string, key: string) => {
        this.featureCallParams.set(key, value);
      });
    }
    return this;
  }

  // returns the first preferred format that is allowed
  protected getFormat(http: HttpClient): Promise<null | WmsFeatureFormat> {
    return (null == this.allowedFormats ? this.populateAllowedFormats(http) : Promise.resolve()).then(() => {
      // console.debug('getFormat', this.preferredFormats, this.allowedFormats);
      return this.preferredFormats.find((preferredFormat) => this.allowedFormats.includes(preferredFormat)) ?? null;
    });
  }

  protected setDefaults(): void {
    const getParams = () => {
      this.featureCallParams.set('info_format', String(this.selectedFormat));
      return this.featureCallParams;
    };

    // JSON
    const jsonGenerator = new JsonFeatureIdentifier(this.layer, (layer: WmsTileLayer, clickEvent) =>
      WmsFeatureDisplayItemGenerator.createUrl(layer, clickEvent, getParams),
    );
    const htmlGenerator = new HtmlFeatureIdentifier(this.layer, (layer: WmsTileLayer, clickEvent) =>
      WmsFeatureDisplayItemGenerator.createUrl(layer, clickEvent, getParams),
    );

    this.itemGenerators.set(WmsFeatureFormat.GEO_JSON, jsonGenerator);
    this.itemGenerators.set(WmsFeatureFormat.JSON, jsonGenerator);
    this.itemGenerators.set(WmsFeatureFormat.HTML_TEXT, htmlGenerator);
    this.itemGenerators.set(WmsFeatureFormat.PLAIN_TEXT, htmlGenerator);

    this.preferredFormats.push(WmsFeatureFormat.GEO_JSON);
    this.preferredFormats.push(WmsFeatureFormat.JSON);
    this.preferredFormats.push(WmsFeatureFormat.HTML_TEXT);
    this.preferredFormats.push(WmsFeatureFormat.PLAIN_TEXT);
  }

  protected populateAllowedFormats(http: HttpClient): Promise<void> {
    return this.layer.getCapabilitiesXml(http).then(($xml: JQuery<XMLDocument>) => {
      // console.debug('test', $xml.find('GetFeatureInfo>Format'));
      const formats = $xml.find('GetFeatureInfo>Format') ?? [];
       
      this.allowedFormats = Array.from(formats)
        .map((element: Element) => {
          return WmsFeatureDisplayItemGenerator.getEnumFromValue<WmsFeatureFormat>(
            String(element.textContent),
            WmsFeatureFormat,
          );
        })
        .filter((item) => null != item) as Array<WmsFeatureFormat>;
    });
  }
}
